/**
 * A design's history.
 *
 * A design that is worked on every week has hundreds of versions, so this screen is
 * built for the long case rather than the short one:
 *
 * - **the list is paged and grouped by day.** Forty at a time, newest first, under
 *   a heading per day — because "how it looked last Tuesday" is how people remember
 *   a version, and a flat list of three hundred timestamps is not. Older versions
 *   load when asked for, and the arrow keys walk the list.
 * - **each entry says what changed, in words**, worked out when the version was
 *   saved, so the list is readable without opening anything.
 * - **only the selected version is opened**: its full list of changes and a preview
 *   of how it looked, drawn with the design's own data and letterhead.
 *
 * Comparison is a choice between two questions, each worded as the question: what
 * changed *in* this version, and what would change *if it were made current*. The
 * second is the one to read before restoring.
 *
 * The preview is the SDK's read-only viewer with its toolbar hidden. The toolbar
 * carries a print button, and a document printed from this tab would be a PDF the
 * free plan's mark never touched — the same reason the designer's own export is off.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createRegistry } from "@broadpaper/blocks";
import { createReportViewer, type ReportViewer } from "@broadpaper/renderer";
import type { ChangeKind } from "@broadpaper/core";
import { api, helpUrl, type RevisionDetail, type RevisionSummary, type TemplateDetail } from "../api.js";
import { reportFailure } from "../App.js";
import { Button, Chip, Confirm, Empty, Icon, Skeleton, at, count, toast } from "../ui/index.js";

const registry = createRegistry();

const KIND_WORDS: Record<ChangeKind, string> = { added: "Added", removed: "Removed", changed: "Changed", moved: "Moved" };
const KIND_ORDER: ChangeKind[] = ["added", "changed", "moved", "removed"];

interface Loaded {
  name: string;
  revisions: RevisionSummary[];
  total: number;
  current: number;
  nextBefore: number | null;
}

export function History({ templateId, selected: initial }: { templateId: string; selected: number | null }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState<number | null>(initial);
  const [compare, setCompare] = useState<"previous" | "current">("previous");
  const [detail, setDetail] = useState<RevisionDetail | null>(null);
  const [design, setDesign] = useState<TemplateDetail | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const list = useRef<HTMLDivElement>(null);

  const loadNewest = useCallback(
    async (select?: number) => {
      const first = await api.revisions(templateId);
      setLoaded({ name: first.template.name, revisions: first.revisions, total: first.total, current: first.current, nextBefore: first.nextBefore });
      setSelected((current) => select ?? current ?? (first.current || null));
    },
    [templateId]
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadNewest(), api.template(templateId).then((d) => !cancelled && setDesign(d))]).catch(
      (e) => !cancelled && setFailed(e instanceof Error ? e.message : "We couldn't open this design's history.")
    );
    return () => {
      cancelled = true;
    };
  }, [templateId, loadNewest]);

  const loadOlder = async () => {
    if (!loaded?.nextBefore) return;
    setMore(true);
    try {
      const page = await api.revisions(templateId, loaded.nextBefore);
      setLoaded({ ...loaded, revisions: [...loaded.revisions, ...page.revisions], nextBefore: page.nextBefore, total: page.total, current: page.current });
    } catch (e) {
      reportFailure(e, "We couldn't load older versions.");
    } finally {
      setMore(false);
    }
  };

  const isCurrent = selected !== null && loaded !== null && selected === loaded.current;
  const mode = isCurrent ? "previous" : compare;

  useEffect(() => {
    if (selected === null) return;
    let cancelled = false;
    setDetail(null);
    api
      .revision(templateId, selected, mode)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && reportFailure(e, "We couldn't open that version."));
    // The address follows the selection without a navigation, so a version can be
    // linked to and a reload lands on it — and without the scroll to the top that
    // a route change would do to somebody half way down a long list.
    window.history.replaceState(null, "", `/designs/${templateId}/history?v=${selected}`);
    return () => {
      cancelled = true;
    };
  }, [templateId, selected, mode]);

  const groups = useMemo(() => {
    const out: Array<{ label: string; items: RevisionSummary[] }> = [];
    for (const revision of loaded?.revisions ?? []) {
      const label = dayLabel(revision.updatedAt);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(revision);
      else out.push({ label, items: [revision] });
    }
    return out;
  }, [loaded]);

  /** Up and down walk the list, and walking off the end loads the next page. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!loaded || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return;
    event.preventDefault();
    const index = loaded.revisions.findIndex((r) => r.number === selected);
    const next = loaded.revisions[index + (event.key === "ArrowDown" ? 1 : -1)];
    if (next) {
      setSelected(next.number);
      list.current?.querySelector<HTMLElement>(`[data-version="${next.number}"]`)?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "ArrowDown" && loaded.nextBefore) void loadOlder();
  };

  const restore = async () => {
    if (selected === null || !loaded) return;
    setConfirming(false);
    setRestoring(true);
    try {
      const result = await api.restoreRevision(templateId, selected);
      if (result.unchanged) {
        toast("The design already looks exactly like that version.", "info");
      } else {
        toast(`Version ${result.restoredFrom} is now the design. What it replaced is still here, as version ${loaded.current}.`, "good");
        setCompare("previous");
        await loadNewest(result.revision ?? undefined);
        list.current?.scrollTo({ top: 0 });
      }
    } catch (e) {
      reportFailure(e, "We couldn't make that version current.");
    } finally {
      setRestoring(false);
    }
  };

  if (failed) {
    return (
      <Empty title="We couldn't open this history" actions={<a className="btn btn--primary" href="/designs">Back to your designs</a>}>
        {failed}
      </Empty>
    );
  }

  if (!loaded) {
    return (
      <div className="wrap--wide stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 40, width: 320 }} />
        <Skeleton rows={1} height={420} />
      </div>
    );
  }

  return (
    <div className="wrap--wide stack" style={{ gap: 20 }}>
      <div className="head" style={{ marginBottom: 0 }}>
        <div className="head__title">
          <span className="eyebrow">
            <a href="/designs" style={{ textDecoration: "none" }}>
              Designs
            </a>{" "}
            · {loaded.name}
          </span>
          <h1>History</h1>
          <p className="head__lede">
            {count(loaded.total)} version{loaded.total === 1 ? "" : "s"}. BroadPaper keeps a version each time you work on this design. Choose one to see how it
            looked and what changed. <a href={helpUrl("/support/designs/#history")} target="_blank" rel="noreferrer">How history works</a>
          </p>
        </div>
        <div className="head__actions">
          <a className="btn" href={`/designs/${templateId}`}>
            <Icon name="design" /> Open the design
          </a>
        </div>
      </div>

      <div className="history">
        <div ref={list} className="history__list" role="listbox" aria-label="Versions" tabIndex={0} onKeyDown={onKeyDown}>
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <div className="history__day">{group.label}</div>
              {group.items.map((revision) => (
                <button
                  key={revision.number}
                  type="button"
                  role="option"
                  aria-selected={revision.number === selected}
                  data-version={revision.number}
                  tabIndex={-1}
                  className={`history__item${revision.number === selected ? " is-selected" : ""}`}
                  onClick={() => setSelected(revision.number)}
                >
                  <span className="spread" style={{ gap: 8 }}>
                    <strong className="small">
                      Version {revision.number}
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {" "}
                        · {new Date(revision.updatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </strong>
                    {revision.current ? <Chip tone="good">Current</Chip> : null}
                  </span>
                  <span className="small history__summary">{reasonWords(revision, design?.template.importedFrom)}</span>
                  {revision.counts && revision.reason === "edit" ? <Counts counts={revision.counts} /> : null}
                </button>
              ))}
            </div>
          ))}
          {loaded.nextBefore ? (
            <div style={{ padding: 12 }}>
              <Button size="sm" block busy={more} onClick={() => void loadOlder()}>
                Show older versions
              </Button>
            </div>
          ) : (
            <p className="small muted" style={{ padding: "12px 14px" }}>
              That's the beginning of this design's history.
            </p>
          )}
        </div>

        <div className="history__detail">
          {!detail ? (
            <Skeleton rows={2} height={200} />
          ) : (
            <>
              <div className="spread" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
                <div className="stack" style={{ gap: 4 }}>
                  <span className="eyebrow">Version {detail.revision.number}</span>
                  <h2>{reasonWords(detail.revision, design?.template.importedFrom)}</h2>
                  <p className="small muted">
                    Saved {at(detail.revision.updatedAt)}
                    {sessionWords(detail.revision)}
                  </p>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {detail.revision.current ? (
                    <Chip tone="good">This is the current design</Chip>
                  ) : (
                    <Button variant="primary" icon="history" busy={restoring} onClick={() => setConfirming(true)}>
                      Make this the current design
                    </Button>
                  )}
                </div>
              </div>

              {!detail.revision.current ? (
                <div className="segmented" role="group" aria-label="Compare with">
                  <button type="button" aria-pressed={mode === "previous"} onClick={() => setCompare("previous")}>
                    What changed in this version
                  </button>
                  <button type="button" aria-pressed={mode === "current"} onClick={() => setCompare("current")}>
                    Compared with the current design
                  </button>
                </div>
              ) : null}

              <section className="card card--flat">
                <div className="card__head">
                  <strong>{mode === "current" ? "What making this version current would change" : "What changed"}</strong>
                  {detail.compare.number ? <span className="small muted">Compared with version {detail.compare.number}</span> : null}
                </div>
                <div className="card__body">
                  <Changes detail={detail} mode={mode} />
                </div>
              </section>

              <section className="stack" style={{ gap: 8 }}>
                <span className="small muted">How version {detail.revision.number} looked, with your current data and letterhead</span>
                {design ? <Preview detail={detail} design={design} /> : <div className="skeleton history__preview" />}
              </section>
            </>
          )}
        </div>
      </div>

      {confirming && selected !== null ? (
        <Confirm title={`Make version ${selected} the current design?`} confirmLabel="Make it current" onCancel={() => setConfirming(false)} onConfirm={() => void restore()}>
          The design will look exactly as it did in version {selected}, and keep its current name. Nothing is lost: what you have now stays in the history as
          version {loaded.current}, so you can go back to it.
        </Confirm>
      ) : null}
    </div>
  );
}

/* ---------------------------------- parts ---------------------------------- */

function Changes({ detail, mode }: { detail: RevisionDetail; mode: "previous" | "current" }) {
  if (!detail.diff) {
    return <p className="muted">This is where the design's history begins, so there's nothing earlier to compare it with.</p>;
  }
  if (detail.diff.identical) {
    return <p className="muted">{mode === "current" ? "This version looks exactly like the current design." : "Nothing visible changed in this version."}</p>;
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      <ul className="changes">
        {detail.diff.changes.map((change, i) => (
          <li key={`${change.nodeId ?? change.area}-${i}`}>
            <span className={`changes__kind changes__kind--${change.kind}`}>{KIND_WORDS[change.kind]}</span>
            <span>
              {change.summary}
              {change.before !== undefined || change.after !== undefined ? (
                <span className="changes__values">
                  {change.before !== undefined ? <del>{change.before || "nothing"}</del> : null}
                  {change.before !== undefined && change.after !== undefined ? " → " : null}
                  {change.after !== undefined ? <ins>{change.after || "nothing"}</ins> : null}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {detail.diff.truncated ? <p className="small muted">…and {count(detail.diff.truncated)} more.</p> : null}
    </div>
  );
}

function Counts({ counts }: { counts: Record<ChangeKind, number> }) {
  const shown = KIND_ORDER.filter((k) => counts[k] > 0);
  if (!shown.length) return null;
  return (
    <span className="history__counts">
      {shown.map((k) => (
        <span key={k} className={`count--${k}`}>
          {counts[k]} {k}
        </span>
      ))}
    </span>
  );
}

/**
 * The version, drawn.
 *
 * One viewer for the life of the screen, updated in place as the selection moves,
 * so stepping through versions re-lays out a template rather than rebuilding the
 * viewer and its scroll position every time.
 */
function Preview({ detail, design }: { detail: RevisionDetail; design: TemplateDetail }) {
  const mount = useRef<HTMLDivElement>(null);
  const viewer = useRef<ReportViewer | null>(null);

  useEffect(() => {
    const element = mount.current;
    if (!element) return;
    if (!viewer.current) {
      viewer.current = createReportViewer(element, {
        template: detail.json,
        registry,
        dataSources: design.dataSources,
        data: design.data,
        theme: design.theme,
        locale: "en-GB",
        currency: "GBP",
        toolbar: false,
        interactions: false,
        fit: "width",
        label: `Version ${detail.revision.number}`
      });
    } else {
      void viewer.current.update({ template: detail.json, label: `Version ${detail.revision.number}` });
    }
  }, [detail, design]);

  useEffect(
    () => () => {
      viewer.current?.destroy();
      viewer.current = null;
    },
    []
  );

  return <div ref={mount} className="history__preview" />;
}

/* --------------------------------- words --------------------------------- */

function reasonWords(revision: RevisionSummary, importedFrom?: string | null): string {
  switch (revision.reason) {
    case "start":
      return "How the design started";
    case "import":
      // The file's extension is the only record of which kind of document it was,
      // and it is the thing the person chose, so it is the thing to name.
      return /\.pdf$/i.test(importedFrom ?? "") ? "Imported from a PDF" : importedFrom ? "Imported from a Word document" : "Imported from a document";
    case "baseline":
      return "As it was before history was kept";
    case "restore":
      return revision.restoredFrom ? `Went back to version ${revision.restoredFrom}` : "Went back to an earlier version";
    default:
      return revision.summary ?? "Edited";
  }
}

/** " · editing began at 13:05", when a version covers a stretch of work rather than a moment. */
function sessionWords(revision: RevisionSummary): string {
  const began = new Date(revision.createdAt).getTime();
  const ended = new Date(revision.updatedAt).getTime();
  if (revision.reason !== "edit" || ended - began < 60_000) return "";
  return ` · editing began at ${new Date(revision.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

/** "Today", "Yesterday", "Wednesday", then "3 September" — how people remember when they worked on something. */
function dayLabel(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(date)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days > 1 && days < 7) return date.toLocaleDateString("en-GB", { weekday: "long" });
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: date.getFullYear() === now.getFullYear() ? undefined : "numeric" });
}
